import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { of } from 'rxjs';
import { catchError, debounceTime, filter, tap } from 'rxjs/operators';

import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { Message } from '../../core/definition.scorecard.model';
import { ScorecardGridOptions } from '../../core/scorecard-grid-options.model';
import { TemplateMode, Widget } from '../../core/template-definition.model';
import {
    bindWidthResize,
    Column,
    Context,
    DivRendererOptions,
    ExperimentalNgGridComponent,
    GridContent,
    SelectTemplateFn,
    SortOrder,
} from '../../experimental.grid';
import { TranslationKeyMap } from '../../scorecard.i18n';
import { ScorecardAttributes } from '../../scorecard.model';

import {
    addColumnsMeasure,
    addRowsMeasure,
    autoSizeGrid,
} from './auto-size-grid';
import { GridEpicBridge } from './grid.epic.bridge';
import {
    CellContext,
    ColumnContext,
    ColumnType,
    RowContext,
} from './grid.model';
import { PerformanceGrid } from './grid.state';
import { ScoredardBridgeService } from './sc-bridge.service';

@Component({
    selector: 'cmps-performance-grid',
    templateUrl: './performance-grid.component.html',
})
@TakeUntilDestroy()
export class PerformanceGridComponent implements OnChanges, OnInit, OnDestroy {
    @ViewChild(ExperimentalNgGridComponent)
    private readonly _grid: ExperimentalNgGridComponent;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _ColumnType = ColumnType;
    /** @private */ _SortOrder = SortOrder;

    @Input() widget: Widget<ScorecardGridOptions>;
    @Input() messageAttributes: ScorecardAttributes[];
    @Input() messages: Message[];

    @Input() mode: TemplateMode;
    @Input() model: PerformanceGrid;
    /** @private */ _prevModel: PerformanceGrid;

    @Output() sortChange: EventEmitter<string /* columnId */>;
    private _blackoutBindWidthResize: boolean;
    public headerColour: string;
    public headerTextColour: string;

    /** @private */ _rendererOpts: DivRendererOptions;
    /** @private */ _selectTpl: SelectTemplateFn = (
        context: Context<
            ColumnContext,
            RowContext,
            ColumnContext | CellContext
        >,
        contents: QueryList<GridContent>,
    ): GridContent => {
        if (context.t === 'grid') {
            return contents.find((e) => e.type === 'grid');
        }
        if (context.t === 'row') {
            return contents.find((e) => e.type === 'row');
        }
        if (context.t === 'cell') {
            return contents.find(
                (e) => e.type === 'cell' && e.id === context.ctx.templateId,
            );
        }
    };

    constructor(
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _scBridge: ScoredardBridgeService,
        private readonly _epicBridge: GridEpicBridge,
    ) {
        this.sortChange = new EventEmitter();
        this.headerColour = this.widget?.headerColour;
        this.headerTextColour = this.widget?.headerTextColour;
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('model')) {
            if (
                (!this._prevModel && this.model) ||
                (this._prevModel &&
                    this.model &&
                    this._prevModel.columns !== this.model.columns)
            ) {
                // TODO revise this breaks the state convention. need to revise api/pattern for the grid
                // this is dirty. will try to improve the grid api
                addColumnsMeasure(this.model.columns);

                this._rendererOpts = {
                    ...this._rendererOpts,
                    columns: this.model.columns,
                };
            }

            if (
                (!this._prevModel && this.model) ||
                (this._prevModel &&
                    this.model &&
                    this._prevModel.rows !== this.model.rows)
            ) {
                // TODO revise this breaks the state convention. need to revise api/pattern for the grid
                // this is dirty. will try to improve the grid api
                addRowsMeasure(this.model.rows);
            }

            if (
                ((!this._prevModel && this.model) ||
                    (this._prevModel &&
                        this.model &&
                        this._prevModel.sortDefs !== this.model.sortDefs)) &&
                this._grid &&
                this._grid.renderer
            ) {
                this._grid.renderer.contentColumns.columns.forEach((r) =>
                    r.cells.forEach((l) => l.detectChanges({ onlySelf: true })),
                );
            }

            this._prevModel = this.model;
        }
    }

    ngOnInit(): void {
        this._rendererOpts = {
            columns: this.model ? this.model.columns : null,
            defaultRenderFn: null,
            height: 350,
            horizontalScrollPadding: 9999,
            verticalScrollPadding: 9999,
        };

        // TODO revise. a problem with the pinned scorecard header +
        //  window scroll height causing the window scrollbar to flicker +
        //  while the window is resizing can cause edge-case weirdness
        bindWidthResize(this._el.nativeElement.parentElement)
            .pipe(
                filter(() => !this._blackoutBindWidthResize),
                tap(() => this._onWillInflate()),
                debounceTime(250),
                tap(() => this._onDidInflate()),
                catchError(() => of(null)),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.sortChange.complete();
    }

    layout(): void {
        this._onWillInflate();
        this._onDidInflate();
    }

    /** @private */ _onSortClick(column: Column<ColumnContext>): void {
        this.sortChange.emit(column.id);
    }

    /** @private */
    _onWillInflate(): void {
        // TODO revise. this is just a mitigation. the real problem is scorecardComponent.stateToProps()
        if (!this._scBridge.componentScorecard.bridgeIsExporting()) {
            this._el.nativeElement.style.visibility = 'hidden';
        }
        this._blackoutBindWidthResize = true;
    }

    /** @private */
    _onDidInflate(): void {
        this._changeDetectorRef.detectChanges();
        if (!this._grid || !this._grid.renderer) {
            return;
        }
        this._epicBridge.renderer_tab_grid = this._grid.renderer;

        // this is dirty. will try to improve the grid api
        const leftColumns = [ColumnType.PILLAR, ColumnType.METRIC_NAME].map(
            String,
        );
        const columns = this.model.columns;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const left =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            columns && columns.filter((e) => leftColumns.includes(e.id));
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const right =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            columns && columns.filter((e) => !leftColumns.includes(e.id));
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        autoSizeGrid(
            this._grid.renderer,
            left,
            right,
            this._el.nativeElement.parentElement,
        ).then(() => {
            this._el.nativeElement.style.visibility = '';
            this._blackoutBindWidthResize = false;
        });
    }
}
